/**
 * custom-atc.js
 * Handles:
 *  - Color swatch selection on product cards
 *  - Size popup (show/hide) on "Add to Cart" click
 *  - Immediate add-to-cart when size is selected
 *  - Direct add-to-cart for single-option (color-only) products
 */

(function () {
  'use strict';

  // ── Helpers ────────────────────────────────────────────────

  /**
   * Find the matching variant given selected option values.
   * @param {Array} variants  — array from JSON data
   * @param {Object} selected — { option1: 'Red', option2: 'M', ... }
   */
  function findVariant(variants, selected) {
    return variants.find((v) => {
      return Object.keys(selected).every((key) => {
        const val = selected[key];
        return val === null || v[key] === val;
      });
    }) || null;
  }

  /**
   * Add a variant to cart via Ajax.
   * @returns {Promise}
   */
  function addToCart(variantId, quantity) {
    quantity = quantity || 1;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity }),
    }).then((res) => {
      if (!res.ok) return res.json().then((d) => Promise.reject(d));
      return res.json();
    });
  }

  /**
   * Dispatch a custom event so the theme's cart drawer/counter can update.
   */
  function notifyCartUpdate() {

  // Update cart count
  fetch('/cart.js')
    .then((res) => res.json())
    .then((cart) => {

      // Update all cart count bubbles
      document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {

        const visibleCount = bubble.querySelector('span[aria-hidden="true"]');
        const hiddenCount = bubble.querySelector('.visually-hidden');

        if (visibleCount) {
          visibleCount.textContent = cart.item_count;
        }

        if (hiddenCount) {
          hiddenCount.textContent = `${cart.item_count} items`;
        }
      });

      // Trigger existing cart drawer open behavior
      const cartTrigger = document.querySelector('#cart-icon-bubble');

      if (cartTrigger) {
  setTimeout(() => {
    cartTrigger.click();
  }, 2000); // 2 second delay
}
    });
}

  // ── Per-card initialiser ──────────────────────────────────

  function initCard(block) {
    if (block.dataset.customAtcInit) return;
    block.dataset.customAtcInit = '1';

    // Parse variant data
    const dataEl = block.querySelector('.custom-atc__variants-data');
    if (!dataEl) return;
    const data = JSON.parse(dataEl.textContent);
    const { variants, color_position, size_position, total_options } = data;

    // State: currently selected option values
    // option1 = first option, option2 = second option, etc.
    const selected = {
      option1: null,
      option2: null,
      option3: null,
    };

    // Seed selection from the first available swatch
    const firstSwatch = block.querySelector('.custom-atc__swatch:not(.is-disabled)');
    if (firstSwatch && color_position) {
      selected['option' + color_position] = firstSwatch.dataset.colorValue;
    }

    // ── Color swatch click ──────────────────────────────────
    block.querySelectorAll('.custom-atc__swatch').forEach((swatch) => {
      swatch.addEventListener('click', () => {
        if (swatch.classList.contains('is-disabled')) return;

        // Update UI
        block.querySelectorAll('.custom-atc__swatch').forEach((s) => {
          s.classList.remove('is-selected');
          s.setAttribute('aria-pressed', 'false');
        });
        swatch.classList.add('is-selected');
        swatch.setAttribute('aria-pressed', 'true');

        // Update state
        selected['option' + color_position] = swatch.dataset.colorValue;

        // Update size popup availability if it's open
        updateSizeAvailability(block, data, selected);
      });
    });

    // ── ATC button click ────────────────────────────────────
    const atcBtn = block.querySelector('[data-action="custom-atc-trigger"]');
    if (!atcBtn) return;

    atcBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation(); // Don't trigger the card link

      const hasSize = atcBtn.dataset.hasSizes === 'true';

      if (hasSize) {
        toggleSizePopup(block, data, selected, true);
      } else {
        // No sizes — find variant by color and add directly
        const variant = findVariant(variants, selected);
        if (!variant) return;
        doAddToCart(atcBtn, variant.id);
      }
    });

    // ── Size button click ───────────────────────────────────
    block.querySelectorAll('.custom-atc__size-btn').forEach((sizeBtn) => {
      sizeBtn.addEventListener('click', () => {

        if (sizeBtn.classList.contains('is-unavailable')) return;

        const optionPosition = sizeBtn.dataset.optionPosition;
        const sizeVal = sizeBtn.dataset.sizeValue;

        // Save selected option
        selected['option' + optionPosition] = sizeVal;

        // Update active UI only inside same group
        const parentGroup = sizeBtn.closest('.custom-atc__size-group');

        parentGroup.querySelectorAll('.custom-atc__size-btn').forEach((btn) => {
          btn.classList.remove('is-selected');
        });

        sizeBtn.classList.add('is-selected');

        // Count selected options
        const selectedCount = Object.values(selected).filter(Boolean).length;

        // Stop if all options not selected yet
        if (selectedCount < total_options) {
          return;
        }

        // Find matching variant
        const variant = findVariant(variants, selected);

        if (!variant || !variant.available) {
          return;
        }

        // Close popup
        toggleSizePopup(block, data, selected, false);

        // Add to cart
        doAddToCart(atcBtn, variant.id);

      });
    });

    // ── Popup close button ──────────────────────────────────
    const closeBtn = block.querySelector('.custom-atc__size-popup-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        toggleSizePopup(block, data, selected, false);
      });
    }

    // ── Close popup on outside click ────────────────────────
    document.addEventListener('click', (e) => {
      if (!block.contains(e.target)) {
        toggleSizePopup(block, data, selected, false);
      }
    });

    // ── Close popup on Escape ───────────────────────────────
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        toggleSizePopup(block, data, selected, false);
      }
    });
  }

  // ── Toggle size popup ───────────────────────────────────────

  function toggleSizePopup(block, data, selected, open) {
    const popup = block.querySelector('.custom-atc__size-popup');
    if (!popup) return;

    if (open) {
      updateSizeAvailability(block, data, selected);
      popup.setAttribute('aria-hidden', 'false');
    } else {
      popup.setAttribute('aria-hidden', 'true');
    }
  }

  // ── Update size button availability based on selected color ─

  function updateSizeAvailability(block, data, selected) {
    const { variants, color_position, size_position } = data;
    if (!size_position) return;

    block.querySelectorAll('.custom-atc__size-btn').forEach((sizeBtn) => {
      const testSelected = Object.assign({}, selected);
      testSelected['option' + size_position] = sizeBtn.dataset.sizeValue;

      const variant = findVariant(variants, testSelected);
      if (!variant || !variant.available) {
        sizeBtn.classList.add('is-unavailable');
        sizeBtn.setAttribute('aria-disabled', 'true');
      } else {
        sizeBtn.classList.remove('is-unavailable');
        sizeBtn.removeAttribute('aria-disabled');
      }
    });
  }

  // ── Add to cart action ──────────────────────────────────────

  function doAddToCart(btn, variantId) {
    btn.classList.add('is-loading');
    btn.disabled = true;

    addToCart(variantId, 1)
      .then((item) => {
        notifyCartUpdate(item);
        btn.classList.remove('is-loading');
        btn.classList.add('is-success');

        const textEl = btn.querySelector('.custom-atc__btn-text');
        const originalText = textEl.textContent;
        textEl.textContent = '✓ Added';

        setTimeout(() => {
          btn.classList.remove('is-success');
          textEl.textContent = originalText;
          btn.disabled = false;
        }, 1800);
      })
      .catch((err) => {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        console.error('Add to cart error:', err);

        // Optionally: show a brief error state
        const textEl = btn.querySelector('.custom-atc__btn-text');
        const originalText = textEl.textContent;
        textEl.textContent = err.description || 'Error — try again';
        setTimeout(() => {
          textEl.textContent = originalText;
        }, 2000);
      });
  }

  // ── Initialise all cards (and observe new ones via MutationObserver) ──

  function initAll() {
    document.querySelectorAll('.custom-atc-block').forEach(initCard);
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when Shopify loads new sections (quick-add modals, predictive search, etc.)
  const observer = new MutationObserver(() => {
    document.querySelectorAll('.custom-atc-block:not([data-custom-atc-init])').forEach(initCard);
  });
  observer.observe(document.body, { childList: true, subtree: true });
})();


document.addEventListener('click', function (e) {
  const swatch = e.target.closest('.custom-atc__swatch');
  if (!swatch) return;

  const block = swatch.closest('.custom-atc-block');
  const colorValue = swatch.dataset.colorValue;
  const optionPosition = swatch.dataset.optionPosition;

  // Update aria/selected state on custom swatches
  block.querySelectorAll('.custom-atc__swatch').forEach(s => {
    s.classList.remove('is-selected');
    s.setAttribute('aria-pressed', 'false');
  });
  swatch.classList.add('is-selected');
  swatch.setAttribute('aria-pressed', 'true');

  // Find the card wrapper this block lives in
  const cardWrapper = swatch.closest('.card-wrapper, [data-product-card-swatches]');
  if (!cardWrapper) return;

  // Find the matching radio input in the working fieldset
  const fieldset = cardWrapper.querySelector(
    `.card-product__swatches[data-option-position="${optionPosition}"]`
  );
  if (!fieldset) return;

  // Find the radio whose label tooltip matches the color value
  const labels = fieldset.querySelectorAll('label.color-swatch');
  labels.forEach(label => {
    if (label.dataset.tooltip === colorValue) {
      // Click the associated radio input
      const inputId = label.getAttribute('for');
      const input = fieldset.querySelector(`#${CSS.escape(inputId)}`);
      if (input) input.click();
    }
  });
});